import type { OntologyField } from 'features/libra/ontology/ontologyFieldTypes'
import { zoneAnchorLabelResolver } from 'features/libra/stepComponents/zoneAnalysis/zoneCommentAnchors'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'

import type { CommentAnchor } from './commentTypes'

export interface AnchorLabelContext {
  workflow: LibraWorkflow
  /** Ticket-scope ontology (top-level fields, no `components` key). */
  ontologyFields: Record<string, OntologyField>
  /**
   * Component-scope ontology (`workflow.globalContext.ontologyFields.components`).
   * Required to resolve labels for anchors whose `componentId` is set —
   * without it, component-scoped anchors fall back to displaying the raw
   * `fieldId`.
   */
  componentOntologyFields?: Record<string, OntologyField>
  /**
   * Map from component id to a display name (e.g. `shell-e38372` → "Shell").
   * When absent, labels use the raw component id.
   */
  componentNames?: Record<string, string>
  stepSpecificData?: Record<string, unknown>
}

export type AnchorLabelResolver = (
  anchor: CommentAnchor,
  context: AnchorLabelContext
) => string | undefined

function getStepName(workflow: LibraWorkflow, stepId: string): string {
  return workflow.steps.find((s) => s.id === stepId)?.name ?? stepId
}

function getFieldName(
  ontologyFields: Record<string, OntologyField>,
  fieldId: string
): string {
  return ontologyFields[fieldId]?.name ?? fieldId
}

/** Suffix added to a field/cell label when the anchor is component-scoped. */
function componentSuffix(
  anchor: { componentId?: string },
  ctx: AnchorLabelContext
): string {
  if (!anchor.componentId) return ''
  const displayName =
    ctx.componentNames?.[anchor.componentId] ?? anchor.componentId
  return ` (${displayName})`
}

/** Pick the ontology map appropriate for a field/cell anchor's scope. */
function scopedOntology(
  anchor: { componentId?: string },
  ctx: AnchorLabelContext
): Record<string, OntologyField> {
  return anchor.componentId
    ? (ctx.componentOntologyFields ?? {})
    : ctx.ontologyFields
}

const coreAnchorLabelResolver: AnchorLabelResolver = (anchor, ctx) => {
  const stepName = getStepName(ctx.workflow, anchor.stepId)

  switch (anchor.type) {
    case 'field': {
      const fieldName = getFieldName(
        scopedOntology(anchor, ctx),
        anchor.fieldId
      )
      return `${stepName} > ${fieldName}${componentSuffix(anchor, ctx)}`
    }

    case 'cell': {
      const fieldName = getFieldName(
        scopedOntology(anchor, ctx),
        anchor.fieldId
      )
      return `${stepName} > ${fieldName}${componentSuffix(anchor, ctx)} > Row ${anchor.rowId.slice(0, 6)} > ${anchor.columnKey}`
    }

    case 'flowchart':
      return anchor.nodeId
        ? `${stepName} > Flowchart: ${anchor.sectionId} > Node: ${anchor.nodeId}`
        : `${stepName} > Flowchart: ${anchor.sectionId}`

    case 'computed':
      return `${stepName} > Computed: ${anchor.sectionId}`

    case 'section':
      return `${stepName} > Section: ${anchor.sectionId}`

    case 'step':
      return stepName

    default:
      return undefined
  }
}

const resolvers: AnchorLabelResolver[] = [
  coreAnchorLabelResolver,
  zoneAnchorLabelResolver,
]

export function getAnchorLabel(
  anchor: CommentAnchor,
  context: AnchorLabelContext
): string {
  for (const resolver of resolvers) {
    const label = resolver(anchor, context)
    if (label) return label
  }
  return `${anchor.stepId} > Unknown element`
}
